import { useMemo } from "react";
import { useQuery } from "react-query";
import { useAppContext } from "../AppProviders";
import { API_URL } from "./helpers";
import {
  FAMA_FEATURE_FLAG_LD_KEYS,
  resolveFamaFlags,
} from "./feature-flags/FeatureFlagResolver";
import type {
  BehaviorMappingActions,
  BulkFeatureFlagValues,
  FamaFlagsTypes,
} from "./feature-flags/FeatureFlagResolver";

export type { BehaviorMappingActions, FamaFlagsTypes };

export const useFamaFlags = (): FamaFlagsTypes => {
  const { requestApi } = useAppContext();

  const five_minutes = 5 * 60 * 1000;
  const { data: bulkData } = useQuery(
    "feature-flags-bulk",
    () =>
      requestApi
        .post(`${API_URL}/feature-flags/bulk`, {
          keys: FAMA_FEATURE_FLAG_LD_KEYS,
        })
        .then((r) => r.data as BulkFeatureFlagValues),
    {
      enabled: !!requestApi,
      staleTime: five_minutes,
      cacheTime: five_minutes,
      retry: 1,
    }
  );

  return useMemo(() => resolveFamaFlags(bulkData), [bulkData]);
};

export function behaviorRelabelMapping(
  behaviorFlag: string[],
  bma: BehaviorMappingActions
): string[] {
  const relabeledBehaviors = [];
  if (bma.useMapper) {
    for (const behavior of behaviorFlag) {
      const upperCasedBehavior = behavior.toUpperCase();
      if (bma.mapper[upperCasedBehavior]) {
        relabeledBehaviors.push(bma.mapper[upperCasedBehavior]);
      } else {
        relabeledBehaviors.push(behavior);
      }
    }
  }

  if (!bma.useMapper) {
    relabeledBehaviors.push(...behaviorFlag);
  }

  if (!bma.showAll) {
    return relabeledBehaviors.filter((behavior) => bma.show.includes(behavior));
  }
  return relabeledBehaviors;
}

export function behaviorMap(
  searchTerm: string,
  bma: BehaviorMappingActions,
  searchBy: "key" | "value"
) {
  if (bma.useMapper) {
    for (const [key, value] of Object.entries(bma.mapper)) {
      if (
        (searchBy === "key" && key === searchTerm) ||
        (searchBy === "value" && value === searchTerm)
      ) {
        return searchBy === "key" ? value : key;
      }
    }
  }
  return searchTerm;
}
